import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { EmploymentType, SavingsProduct } from './RetirementCalculator.const';
import { ContributionLimitsSourceModel, YearlyContributionLimitsModel } from './RetirementCalculator.model';

export const CURRENT_LIMIT_URL = '/api/funds/currentLimit';
export const CONTRIBUTION_LIMIT_URL = '/api/funds/contributionLimit';

interface CurrentLimitResponseModel {
  currentLimits?: Partial<
    Record<'IKE' | 'IKZE', { limit?: number | null; entrepreneurLimit?: number | null }>
  >;
}

interface ContributionLimitResponseModel {
  limits?: Partial<Record<'PPE', { yearlyLimit?: number | null }>>;
}

interface ContributionLimitsResponsesModel {
  currentLimits: CurrentLimitResponseModel;
  contributionLimits: ContributionLimitResponseModel;
}

const toLimit = (value?: number | null) => (typeof value === 'number' ? value : undefined);

const getIkzeLimit = (
  limits: CurrentLimitResponseModel['currentLimits'],
  employmentType?: EmploymentType,
) => {
  const ikze = limits?.IKZE;
  return employmentType === EmploymentType.BUSINESS
    ? toLimit(ikze?.entrepreneurLimit) ?? toLimit(ikze?.limit)
    : toLimit(ikze?.limit);
};

const toYearlyLimits = (
  currentLimits: CurrentLimitResponseModel,
  contributionLimits: ContributionLimitResponseModel,
  employmentType?: EmploymentType,
): YearlyContributionLimitsModel => {
  const limits: YearlyContributionLimitsModel = {};
  const ike = toLimit(currentLimits.currentLimits?.IKE?.limit);
  const ikze = getIkzeLimit(currentLimits.currentLimits, employmentType);
  const ppe = toLimit(contributionLimits.limits?.PPE?.yearlyLimit);

  if (ike !== undefined) {
    limits[SavingsProduct.IKE] = ike;
  }
  if (ikze !== undefined) {
    limits[SavingsProduct.IKZE] = ikze;
  }
  if (ppe !== undefined) {
    limits[SavingsProduct.PPE] = ppe;
  }

  return limits;
};

const resolveFallbackLimits = (
  fallbackLimits: ContributionLimitsSourceModel['fallbackLimits'],
  employmentType: EmploymentType | undefined,
) => {
  if (!fallbackLimits) {
    return undefined;
  }

  const { ikzeEntrepreneur, ...yearlyLimits } = fallbackLimits;
  return employmentType === EmploymentType.BUSINESS && ikzeEntrepreneur !== undefined
    ? {
        ...yearlyLimits,
        [SavingsProduct.IKZE]: ikzeEntrepreneur,
      }
    : yearlyLimits;
};

export const useContributionLimits = (
  baseUrl: ContributionLimitsSourceModel['baseUrl'],
  disabled: ContributionLimitsSourceModel['disabled'],
  fallbackLimits: ContributionLimitsSourceModel['fallbackLimits'],
  employmentType: EmploymentType | undefined,
): YearlyContributionLimitsModel | undefined => {
  const resolvedFallbackLimits = useMemo(
    () => resolveFallbackLimits(fallbackLimits, employmentType),
    [employmentType, fallbackLimits],
  );
  const [responses, setResponses] = useState<ContributionLimitsResponsesModel>();

  useEffect(() => {
    if (disabled) {
      setResponses(undefined);
      return undefined;
    }

    let isActive = true;
    const config = baseUrl ? { baseURL: baseUrl } : undefined;
    setResponses(undefined);

    Promise.all([
      axios.get<CurrentLimitResponseModel>(CURRENT_LIMIT_URL, config),
      axios.get<ContributionLimitResponseModel>(CONTRIBUTION_LIMIT_URL, config),
    ])
      .then(([currentLimits, contributionLimits]) => {
        if (isActive) {
          setResponses({
            currentLimits: currentLimits.data,
            contributionLimits: contributionLimits.data,
          });
        }
      })
      .catch(() => {
        if (isActive) {
          setResponses(undefined);
        }
      });

    return () => {
      isActive = false;
    };
  }, [baseUrl, disabled]);

  const fetchedLimits = useMemo(
    () =>
      responses
        ? toYearlyLimits(
            responses.currentLimits,
            responses.contributionLimits,
            employmentType,
          )
        : undefined,
    [employmentType, responses],
  );

  const limits = useMemo(
    () =>
      fetchedLimits === undefined
        ? resolvedFallbackLimits
        : { ...resolvedFallbackLimits, ...fetchedLimits },
    [fetchedLimits, resolvedFallbackLimits],
  );

  return disabled ? resolvedFallbackLimits : limits;
};
